import type { TrackingCore } from "./core";

/**
 * Growth / acquisition & viral-loop events: landing entry choice, app-store
 * badges, the mobile-app interstitial banner, referral links, and the +1 invite
 * UX. Merged onto the tracking singleton in index.ts.
 */
export const growthEvents = {
  // ─── Landing entry choice ─────────────────────────────────────────────────
  landingHeadFirst(this: TrackingCore) {
    this.track("head_first");
  },

  landingHeartFirst(this: TrackingCore) {
    this.track("heart_first");
  },

  // ─── App store ────────────────────────────────────────────────────────────
  appStoreClicked(this: TrackingCore, source: string) {
    this.track("app_store_clicked", { source });
  },

  playStoreClicked(this: TrackingCore, source: string) {
    this.track("play_store_clicked", { source });
  },

  installAppBannerShown(this: TrackingCore, source: string) {
    this.track("install_app_banner_shown", { source });
  },

  // ─── Push permission primer ───────────────────────────────────────────────
  // The soft ask that runs before the OS sheet. Worth measuring on its own:
  // primer-shown → allowed is the number that says whether the wording is
  // doing its job, and it is invisible from the OS-level grant rate alone.
  pushPrimerShown(this: TrackingCore) {
    this.track("push_primer_shown");
  },

  pushPrimerAllowed(this: TrackingCore) {
    this.track("push_primer_allowed");
  },

  pushPrimerDismissed(this: TrackingCore) {
    this.track("push_primer_dismissed");
  },

  // ─── Referral ─────────────────────────────────────────────────────────────
  copyReferralLink(this: TrackingCore) {
    this.track("copy_referral_link");
  },

  shareReferralLink(this: TrackingCore) {
    this.track("share_referral_link");
  },

  // ─── +1 invites ───────────────────────────────────────────────────────────
  // +1 invite UX events. Server-side counterparts fire in routes/user/invites.ts
  // — these client-side events capture intent and pre-auth visits the server
  // can't see (e.g. someone landing on /invite/:code without an account yet).
  inviteSectionToggled(this: TrackingCore, bookingId: string, opened: boolean) {
    this.track("invite_section_toggled", {
      booking_id: bookingId,
      opened,
    });
  },

  inviteSendClicked(this: TrackingCore, bookingId: string) {
    this.track("invite_send_clicked", {
      booking_id: bookingId,
    });
  },

  inviteLinkCopied(this: TrackingCore, inviteId: string, source: "create" | "list") {
    this.track("invite_link_copied", {
      invite_id: inviteId,
      source,
    });
  },

  inviteCancelClicked(this: TrackingCore, inviteId: string, status: string) {
    this.track("invite_cancel_clicked", {
      invite_id: inviteId,
      previous_status: status,
    });
  },

  invitePreviewViewed(
    this: TrackingCore,
    code: string,
    status: string | null,
    signedIn: boolean,
  ) {
    this.track("invite_preview_viewed", {
      invite_code: code,
      invite_status: status,
      signed_in: signedIn,
    });
  },

  inviteAcceptClicked(this: TrackingCore, code: string, signedIn: boolean) {
    this.track("invite_accept_clicked", {
      invite_code: code,
      signed_in: signedIn,
    });
  },

  inviteAcceptResult(
    this: TrackingCore,
    code: string,
    result: "accepted" | "needs_payment" | "error",
  ) {
    this.track("invite_accept_result", {
      invite_code: code,
      result,
    });
  },
};
